import { useMemo, useState, type ReactElement, type ReactNode } from 'react';
import { useFeaturesPageViewModel } from '@/features/features/hooks/useFeaturesPageViewModel';
import { FeaturesTablePanel } from '@/features/features/pages/FeaturesTablePanel';
import { StatusMetricCardsRow } from '@/features/metrics/components/StatusMetricCardsRow';
import {
  formatRangeWindowNoDataText,
  resolveQualityComparisonWindowLabel,
  useQualityDateRange,
} from '@/features/quality/hooks';
import { resolveGenericStatusCardIcon } from '@/features/shared/components/chartPanels/StackedMetricsStatusChartSection.icons';
import { SearchTagsDateHeaderFiltersNext } from '@/features/shared/components/entityTrends/SearchTagsDateHeaderFiltersNext';
import { featureStatusOptions } from '@/shared/constants/statusOptions';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { ListPageTemplate } from '@/ui/composites/scaffolds';
import { FeaturesIcon, Text } from '@/ui/primitives';
import type { FeatureStatus } from '@flowqa/shared';

const resolveFeaturesStatusCardIcon = (statusId: string): typeof FeaturesIcon => {
  if (statusId === 'total') return FeaturesIcon;
  return resolveGenericStatusCardIcon(statusId);
};

const resolveSummaryCount = (value: ReactNode): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value !== 'string') return null;
  const parsedValue = Number.parseInt(value.replaceAll(',', ''), 10);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

export const FeaturesPage = (): ReactElement => {
  const {
    range,
    setRange,
    selectedStatuses,
    selectedTags,
    selectedProjects,
    tagOptions,
    tagOptionsLoading,
    projectFilterOptions,
    showProjectFilter,
    onStatusesChange,
    onTagsChange,
    onProjectsChange,
    tableFilters,
    featureMetricCards,
    featureMetricCardsLoading,
  } = useFeaturesPageViewModel();
  const [searchValue, setSearchValue] = useState('');
  const { rangeStartDay, rangeEndDay } = useQualityDateRange(range);
  const comparisonWindowLabel = resolveQualityComparisonWindowLabel(range);
  const totalMetricCard = featureMetricCards.find((card) => card.id === 'total');
  const summaryCount = resolveSummaryCount(totalMetricCard?.count);
  const tableProjectOptions = useMemo(
    () =>
      projectFilterOptions.map((option) => ({
        id: option.value,
        name: option.label,
        badgeChars: '',
        badgeColor: 'neutral' as const,
        badgeEnabled: false,
      })),
    [projectFilterOptions],
  );
  const tableNoRowsText = formatRangeWindowNoDataText(
    getEmptyStateMessage('entity.noneFound', { entity: 'feature' }),
    range,
    false,
  );

  return (
    <ListPageTemplate
      headerActionsMode="separate"
      title={
        <Text role="pageTitle" component="h1">
          Features
        </Text>
      }
      headerActions={
        <SearchTagsDateHeaderFiltersNext
          searchAriaLabel="Search features"
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          selectedStatuses={selectedStatuses}
          statusOptions={featureStatusOptions}
          onStatusesChange={(next) => {
            onStatusesChange(next as FeatureStatus[]);
          }}
          selectedTags={selectedTags}
          tagOptions={tagOptions}
          tagOptionsLoading={tagOptionsLoading}
          onTagsChange={onTagsChange}
          selectedProjects={showProjectFilter ? selectedProjects : undefined}
          onProjectsChange={showProjectFilter ? onProjectsChange : undefined}
          projectOptions={showProjectFilter ? projectFilterOptions : undefined}
          range={range}
          onRangeChange={setRange}
          pickerStartDay={rangeStartDay}
          pickerEndDay={rangeEndDay}
          hasCustomDates={false}
          isLatestMode={range === 'latest'}
          onSelectedStartDayChange={() => undefined}
          onSelectedEndDayChange={() => undefined}
          includeLatest={false}
          showCustomDateRange={false}
          summaryCount={summaryCount}
          summaryStartDay={rangeStartDay}
          summaryEndDay={rangeEndDay}
          summarySingularLabel="feature"
          summaryPluralLabel="features"
          summaryEmptyText={tableNoRowsText}
          summaryLoading={featureMetricCardsLoading}
        />
      }
      summary={
        <StatusMetricCardsRow
          cards={featureMetricCards}
          loading={featureMetricCardsLoading}
          comparisonWindowLabel={comparisonWindowLabel}
          resolveStatusCardIcon={resolveFeaturesStatusCardIcon}
        />
      }
      content={
        <FeaturesTablePanel
          fixedFilters={tableFilters}
          rangeStartDay={rangeStartDay}
          rangeEndDay={rangeEndDay}
          noRowsText={tableNoRowsText}
          projectOptions={tableProjectOptions}
          searchValue={searchValue}
          tagOptions={tagOptions}
        />
      }
      paddingBottom="lg"
    />
  );
};
